import { X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useWebsiteT } from '../hooks/use-website-t';
import type { CanvasHostStatus } from '../hooks/use-storefront-canvas-host';

export interface FrameStatusStatesProps {
  readonly status: CanvasHostStatus;
  readonly onRetry: () => void;
}

/**
 * Over the frame while it is not showing the storefront: a skeleton while it
 * loads, and an in-frame error with Retry when it could not load. The iframe
 * stays mounted underneath, because the canvas inside it is what makes the
 * frame ready.
 */
export function FrameStatusStates({
  status,
  onRetry,
}: FrameStatusStatesProps): React.JSX.Element | null {
  const { t } = useWebsiteT();
  if (status === 'ready') return null;

  if (status === 'error') {
    return (
      <div
        role="alert"
        data-testid="frame-error"
        className="bg-card/95 absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 px-6 text-center"
      >
        <p className="text-foreground text-sm">{t('admin.websiteTemplates.frame.loadError')}</p>
        <Button type="button" variant="outline" onClick={onRetry}>
          {t('admin.websiteTemplates.common.retry')}
        </Button>
      </div>
    );
  }

  return (
    <div
      role="status"
      data-testid="frame-loading"
      className="bg-card absolute inset-0 z-10 flex flex-col gap-4 p-6"
    >
      <Skeleton className="h-10 w-full" />
      <Skeleton className="h-64 w-full" />
      <div className="grid grid-cols-3 gap-4">
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
        <Skeleton className="h-32" />
      </div>
      <p className="text-muted-foreground text-center text-sm">
        {t('admin.websiteTemplates.frame.loading')}
      </p>
    </div>
  );
}

export interface FrameResetNoticeProps {
  readonly onDismiss: () => void;
}

/** The frame was reloaded because its document changed; saved changes are safe. */
export function FrameResetNotice({ onDismiss }: FrameResetNoticeProps): React.JSX.Element {
  const { t } = useWebsiteT();
  return (
    <div
      role="status"
      data-testid="frame-reset-notice"
      className="border-border bg-admin-lime-light text-foreground flex items-center justify-between gap-3 border-b px-4 py-2 text-sm"
    >
      <span>{t('admin.websiteTemplates.frame.resetNotice')}</span>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="size-7"
        aria-label={t('admin.websiteTemplates.common.close')}
        onClick={onDismiss}
      >
        <X aria-hidden="true" className="size-4" />
      </Button>
    </div>
  );
}
